/**
 * Home Page — Route Module
 *
 * Replaces app/[locale]/(marketing)/page.tsx.
 *
 * The loader fetches home data via Connect-RPC on the server (BFF pattern),
 * normalizes protobuf responses to plain JSON (BigInt → number), and returns
 * serialization-safe data for React Router's hydration.
 */
import { routes } from '@letsgethome/routes';
import { PageContainer } from '@letsgethome/ui/page-container';
import { useLoaderData } from 'react-router';

import { SeoHead } from '../../../../shared/components/SeoHead';
import { useTranslations } from '../../../../shared/i18n/useTranslations';
import { PostsCarousel } from '../../blog/components/PostsCarousel';
import { ListingCarousel } from '../../listings/components/ListingCarousel';
import { DockableHeroSearch } from '../components/DockableHeroSearch';
import { FaqsSection, homePageFaqs } from '../components/FaqsSection';
import { HeroSection } from '../components/HeroSection';
import { WhyUsSection } from '../components/WhyUsSection';
import type { loader } from './home.loader';

export function Component() {
  const { rentals, sales, posts, seo } = useLoaderData<typeof loader>();
  const tCarousel = useTranslations('listings.carousel');
  const tTips = useTranslations('home.tips');
  // FAQPage JSON-LD for the visible FaqsSection, appended to the backend's
  // structured data (RealEstateAgent card) — same FAQ_KEYS the section renders,
  // so structured data and visible content stay in lockstep (ADR 0142 G4).
  const faqs = homePageFaqs(useTranslations('home.faqs').dyn);

  return (
    <>
      {/* Server-computed home SEO (ADR 0142 G3): localized title/description,
          canonical/hreflang, og:image, and the RealEstateAgent JSON-LD card.
          The FAQPage card (G4) is merged in for the visible FaqsSection. */}
      <SeoHead {...seo} structuredData={[...(seo.structuredData ?? []), faqs.jsonLd]} />

      <div className="min-h-screen">
        <HeroSection searchSlot={<DockableHeroSearch />} />

        <PageContainer size="7xl" className="py-12 sm:px-6 lg:px-8">
          <div className="space-y-16">
            <ListingCarousel
              title={tCarousel('forRent')}
              seeAllHref={routes.listings.rent()}
              listings={rentals}
              transactionType="rent"
            />
            <ListingCarousel
              title={tCarousel('forSale')}
              seeAllHref={routes.listings.buy()}
              listings={sales}
              transactionType="sale"
            />
            <PostsCarousel title={tTips('title')} seeAllHref={routes.blog.index()} posts={posts} />
          </div>
        </PageContainer>

        <WhyUsSection />
        <FaqsSection faqs={faqs} />
      </div>
    </>
  );
}
